import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  Textarea,
  VStack,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { format } from "date-fns";
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  FileSignature,
  Info,
  ShieldOff,
  XCircle,
} from "lucide-react";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppLogo } from "components/ds";
import ResourceIcon from "pages/Members/AccessReviews/campaigns/ResourceIcon";
import {
  MFAttestation,
  MFAttestationStatement,
  MFAttestationStatus,
  MFAttestationSummary,
  MF_ACCESS_REVIEWS,
} from "state/members/accessReviews";
import { reviewSectionCopy } from "./detailCopy";
import { attestationSubtitle, attestationTitle } from "./headerCopy";
import { isOwnerSignOff } from "./signOff";
import AttestationEvidence from "./AttestationEvidence";
import {
  EVIDENCE_BODY,
  EVIDENCE_KICKER,
  evidenceEditable,
  showsEvidencePanel,
} from "./evidenceCopy";
import StatementCard from "./StatementCard";
import StatusBadge from "./StatusBadge";

// ── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso?: string | null) =>
  iso ? format(new Date(iso), "do MMMM yyyy") : "-";

const formatDateTime = (iso?: string | null) =>
  iso ? format(new Date(iso), "do MMMM yyyy 'at' h:mm a") : "-";

// Sort statements by their `order` field (ascending) so the experience matches what
// admins configured in the campaign.
const sortStatements = (statements: MFAttestationStatement[]) =>
  [...statements].sort((a, b) => a.order - b.order);

const READ_ONLY_STATUSES: MFAttestationStatus[] = [
  "attested",
  "declined",
  "superseded",
  "skipped_no_assignee",
];

const isReadOnly = (status: MFAttestationStatus) =>
  READ_ONLY_STATUSES.includes(status);

// ── sub-components ───────────────────────────────────────────────────────────

const BackLink = memo(() => (
  <HStack
    as={Link}
    to="/members/attestations"
    aria-label="Back to attestations"
    spacing={2}
    align="center"
    color="text.body.tertiary"
    fontSize="13px"
    fontWeight="500"
    // Ensure ≥44px tap target on touch devices without changing visual layout.
    minH="44px"
    py={2}
    mx={-2}
    px={2}
    borderRadius="6px"
    transitionProperty="color, background-color"
    transitionDuration="120ms"
    transitionTimingFunction="ease-out"
    _hover={{ color: "text.body.primary" }}
    _focusVisible={{
      outline: "none",
      color: "text.body.primary",
      boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
    }}
    sx={{
      // Tighten the icon visually to the label without animating layout.
      "&:hover svg": { transform: "translateX(-2px)" },
      "& svg": {
        transitionProperty: "transform",
        transitionDuration: "120ms",
        transitionTimingFunction: "ease-out",
      },
    }}
  >
    <Icon as={ArrowLeft} boxSize="14px" aria-hidden="true" />
    <Text>Back to attestations</Text>
  </HStack>
));
BackLink.displayName = "BackLink";

type SummaryCount = {
  label: string;
  count: number;
  color: string;
  bg: string;
  borderColor: string;
};

const ReviewSummaryBlock = memo(
  ({
    summary,
  }: {
    summary?: MFAttestation["reviewSummary"];
  }) => {
    if (!summary) {
      return (
        <Box
          w="full"
          bg="neutral.50"
          border="1px dashed"
          borderColor="border.default"
          borderRadius="12px"
          px={{ base: 4, md: 5 }}
          py={{ base: 4, md: 4 }}
        >
          <HStack spacing={3} align="flex-start">
            <Icon
              as={Info}
              boxSize="18px"
              color="text.body.tertiary"
              mt="2px"
            />
            <Text
              fontSize="13px"
              color="text.body.tertiary"
              lineHeight="1.5"
              sx={{ textWrap: "pretty" }}
            >
              Review details aren’t available. Contact your admin if you need
              more context before signing.
            </Text>
          </HStack>
        </Box>
      );
    }

    const counts: SummaryCount[] = [
      {
        label: "Total accounts",
        count: summary.totalAccounts,
        color: "text.body.primary",
        bg: "neutral.50",
        borderColor: "border.default",
      },
      {
        label: "Appropriate",
        count: summary.appropriate,
        color: "success.700",
        bg: "success.50",
        borderColor: "success.200",
      },
      {
        label: "Not required",
        count: summary.notRequired,
        color: "warning.700",
        bg: "warning.50",
        borderColor: "warning.200",
      },
      {
        label: "Out of scope",
        count: summary.outOfScope,
        color: "neutral.700",
        bg: "neutral.100",
        borderColor: "border.default",
      },
      {
        label: "Removed",
        count: summary.removed,
        color: "error.700",
        bg: "error.50",
        borderColor: "error.200",
      },
    ];

    return (
      <Flex
        w="full"
        gap={{ base: 2, md: 3 }}
        flexWrap="wrap"
        // Concentric: outer card padding 16-20px, inner cells 12px radius.
      >
        {counts.map((c) => (
          <Box
            key={c.label}
            flex="1 1 140px"
            minW={{ base: "calc(50% - 4px)", md: "140px" }}
            bg={c.bg}
            border="1px solid"
            borderColor={c.borderColor}
            borderRadius="12px"
            px={4}
            py={3}
          >
            <Text
              fontSize="11px"
              fontWeight="600"
              color="text.body.tertiary"
              letterSpacing="0.04em"
              textTransform="uppercase"
              mb="4px"
            >
              {c.label}
            </Text>
            <Text
              fontFamily="HubotSans, sans-serif"
              fontSize={{ base: "22px", md: "26px" }}
              fontWeight="500"
              color={c.color}
              lineHeight="1.2"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {c.count}
            </Text>
          </Box>
        ))}
      </Flex>
    );
  },
);
ReviewSummaryBlock.displayName = "ReviewSummaryBlock";

// Drill-in to the actual review so attestors can see the accounts + decisions
// before signing. The 5 summary counts above tell you _how many_, not
// _what_ — when you're attesting from the attestations tab (rather than
// inline inside the review), this is the only way to see the decisions
// the reviewer actually made.
const ReviewDrilldownLink = memo(
  ({ reviewId }: { reviewId: string }) => (
    <Link
      to={`/members/access-reviews/reviews/${reviewId}`}
      aria-label="Open the full access review in a new view"
      style={{ textDecoration: "none", display: "block" }}
    >
      <HStack
        role="group"
        w="full"
        bg="bg.container"
        border="1px solid"
        borderColor="border.default"
        borderRadius="12px"
        px={{ base: 4, md: 5 }}
        py={4}
        spacing={3}
        align="center"
        justify="space-between"
        minH="56px"
        transitionProperty="border-color, transform"
        transitionDuration="120ms"
        transitionTimingFunction="ease-out"
        _hover={{ borderColor: "border.hover" }}
        _focusVisible={{
          outline: "none",
          borderColor: "border.focus",
          boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
        }}
        _active={{ transform: "scale(0.996)" }}
      >
        <HStack spacing={3} align="center" flex="1" minW={0}>
          <Flex
            align="center"
            justify="center"
            flexShrink={0}
            w="36px"
            h="36px"
            borderRadius="10px"
            bg="p.50"
            border="1px solid"
            borderColor="p.100"
          >
            <Icon as={FileSignature} boxSize="16px" color="p.600" />
          </Flex>
          <VStack align="flex-start" spacing="2px" minW={0}>
            <Text
              fontSize="14px"
              fontWeight="600"
              color="text.body.primary"
              lineHeight="1.3"
            >
              View the full review
            </Text>
            <Text
              fontSize="12px"
              color="text.body.tertiary"
              lineHeight="1.4"
            >
              See the individual accounts and decisions the reviewer made.
            </Text>
          </VStack>
        </HStack>
        <Icon
          as={ArrowUpRight}
          boxSize="18px"
          color="text.body.tertiary"
          transitionProperty="transform, color"
          transitionDuration="120ms"
          transitionTimingFunction="ease-out"
          _groupHover={{
            transform: "translate(1px, -1px)",
            color: "text.body.primary",
          }}
        />
      </HStack>
    </Link>
  ),
);
ReviewDrilldownLink.displayName = "ReviewDrilldownLink";

const StatusContextBanner = memo(
  ({ attestation }: { attestation: MFAttestation }) => {
    const { status, attestedAt, declinedAt, declineReason } = attestation;

    if (status === "attested") {
      return (
        <Box
          w="full"
          bg="success.50"
          border="1px solid"
          borderColor="success.200"
          borderRadius="12px"
          px={{ base: 4, md: 5 }}
          py={4}
        >
          <HStack spacing={3} align="flex-start">
            <Icon
              as={CheckCircle2}
              boxSize="20px"
              color="success.600"
              mt="2px"
            />
            <VStack align="flex-start" spacing={1}>
              <Text
                fontSize="14px"
                fontWeight="600"
                color="success.700"
                lineHeight="1.4"
              >
                You signed this attestation
              </Text>
              <Text fontSize="13px" color="success.700" lineHeight="1.5">
                Signed on {formatDateTime(attestedAt)}.
              </Text>
            </VStack>
          </HStack>
        </Box>
      );
    }

    if (status === "declined") {
      return (
        <Box
          w="full"
          bg="error.50"
          border="1px solid"
          borderColor="error.200"
          borderRadius="12px"
          px={{ base: 4, md: 5 }}
          py={4}
        >
          <HStack spacing={3} align="flex-start">
            <Icon as={XCircle} boxSize="20px" color="error.500" mt="2px" />
            <VStack align="flex-start" spacing={2} flex="1">
              <VStack align="flex-start" spacing={1}>
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="error.700"
                  lineHeight="1.4"
                >
                  You declined this attestation
                </Text>
                <Text fontSize="13px" color="error.700" lineHeight="1.5">
                  Declined on {formatDateTime(declinedAt)}.
                </Text>
              </VStack>
              {declineReason && (
                <Box
                  w="full"
                  bg="bg.container"
                  border="1px solid"
                  borderColor="error.200"
                  borderRadius="8px"
                  px={3}
                  py={2}
                >
                  <Text
                    fontSize="11px"
                    fontWeight="600"
                    color="text.body.tertiary"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    mb="4px"
                  >
                    Reason
                  </Text>
                  <Text
                    fontSize="13px"
                    color="text.body.primary"
                    lineHeight="1.5"
                    sx={{ textWrap: "pretty" }}
                  >
                    {declineReason}
                  </Text>
                </Box>
              )}
            </VStack>
          </HStack>
        </Box>
      );
    }

    if (status === "skipped_no_assignee") {
      return (
        <Box
          w="full"
          bg="neutral.100"
          border="1px solid"
          borderColor="border.default"
          borderRadius="12px"
          px={{ base: 4, md: 5 }}
          py={4}
        >
          <HStack spacing={3} align="flex-start">
            <Icon
              as={ShieldOff}
              boxSize="20px"
              color="text.body.tertiary"
              mt="2px"
            />
            <VStack align="flex-start" spacing={1}>
              <Text
                fontSize="14px"
                fontWeight="600"
                color="text.body.primary"
                lineHeight="1.4"
              >
                This attestation was skipped
              </Text>
              <Text fontSize="13px" color="text.body.secondary" lineHeight="1.5">
                No attestor could be resolved. An admin will sign on your
                behalf.
              </Text>
            </VStack>
          </HStack>
        </Box>
      );
    }

    if (status === "superseded") {
      return <SupersededBanner attestation={attestation} />;
    }

    return null;
  },
);
StatusContextBanner.displayName = "StatusContextBanner";

// Pulls the most recent active attestation (`:pending`, `:attested`,
// `:skipped_no_assignee`) for the same review + set so the reviewer who
// landed on the now-superseded URL can navigate to the row that actually
// needs their attention.
const SupersededBanner = ({
  attestation,
}: {
  attestation: MFAttestation;
}) => {
  const { data } = MF_ACCESS_REVIEWS.useListMyAttestations();
  const replacement = useMemo<MFAttestationSummary | null>(() => {
    return (
      data.find(
        (a) =>
          a.id !== attestation.id &&
          a.accessReviewId === attestation.accessReviewId &&
          a.accessReviewAccountSetId === attestation.accessReviewAccountSetId &&
          (a.status === "pending" ||
            a.status === "attested" ||
            a.status === "skipped_no_assignee"),
      ) || null
    );
  }, [data, attestation]);

  return (
    <Box
      w="full"
      bg="neutral.100"
      border="1px solid"
      borderColor="border.default"
      borderRadius="12px"
      px={{ base: 4, md: 5 }}
      py={4}
    >
      <HStack spacing={3} align="flex-start">
        <Icon as={Info} boxSize="20px" color="text.body.tertiary" mt="2px" />
        <VStack align="flex-start" spacing={2}>
          <VStack align="flex-start" spacing={1}>
            <Text
              fontSize="14px"
              fontWeight="600"
              color="text.body.primary"
              lineHeight="1.4"
            >
              Superseded by a newer attestation
            </Text>
            <Text fontSize="13px" color="text.body.secondary" lineHeight="1.5">
              This attestation has been replaced. No further action is needed
              here.
            </Text>
          </VStack>
          {replacement && (
            <Link
              to={`/members/attestations/${replacement.id}`}
              style={{ textDecoration: "underline" }}
            >
              <Text
                fontSize="13px"
                fontWeight="600"
                color="primary.600"
                lineHeight="1.4"
              >
                View the latest attestation →
              </Text>
            </Link>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

// ── action area for pending attestations ─────────────────────────────────────

const PendingActionArea = memo(
  ({ attestation }: { attestation: MFAttestation }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const declineTextareaRef = useRef<HTMLTextAreaElement>(null);

    const sortedStatements = useMemo(
      () => sortStatements(attestation.statements),
      [attestation.statements],
    );

    // Track which statement IDs the attestor has confirmed locally.
    const [confirmed, setConfirmed] = useState<Set<string>>(() => {
      const initial = new Set<string>();
      sortedStatements.forEach((s) => {
        if (s.confirmed) initial.add(s.id);
      });
      return initial;
    });

    const [showDecline, setShowDecline] = useState(false);
    const [declineReason, setDeclineReason] = useState("");

    const sign = MF_ACCESS_REVIEWS.useSignAttestation(attestation.id);
    const decline = MF_ACCESS_REVIEWS.useDeclineAttestation(attestation.id);

    const requiredIds = useMemo(
      () => sortedStatements.filter((s) => s.required).map((s) => s.id),
      [sortedStatements],
    );

    const allRequiredConfirmed = useMemo(
      () => requiredIds.every((id) => confirmed.has(id)),
      [requiredIds, confirmed],
    );

    const handleToggleStatement = useCallback(
      (id: string) => (next: boolean) => {
        setConfirmed((prev) => {
          const copy = new Set(prev);
          if (next) copy.add(id);
          else copy.delete(id);
          return copy;
        });
      },
      [],
    );

    const handleSign = useCallback(async () => {
      if (!allRequiredConfirmed || sign.isLoading) return;
      try {
        await sign.mutate(Array.from(confirmed));
        toast({
          status: "success",
          title: "Attestation signed",
          description: "Thank you. Your signature has been recorded. The review is complete once your admin approves it.",
          variant: "subtle",
          duration: 4000,
          isClosable: true,
        });
        // Stay on the detail page rather than redirecting back to the now-empty
        // pending list. The mutation invalidates the attestation cache so the
        // page refetches into its `:attested` read-only state (with statement
        // checkmarks + StatusContextBanner) — which acts as the signed
        // receipt the reviewer can bookmark or share with their admin.
      } catch {
        // useMemberMutate already surfaces an error toast on failure.
      }
    }, [allRequiredConfirmed, confirmed, sign, toast]);

    const trimmedReason = declineReason.trim();
    const reasonValid = trimmedReason.length >= 10;

    const handleSubmitDecline = useCallback(async () => {
      if (!reasonValid || decline.isLoading) return;
      try {
        await decline.mutate(trimmedReason);
        toast({
          status: "info",
          title: "Attestation declined",
          description:
            "Your admin has been notified and will follow up on the review.",
          variant: "subtle",
          duration: 4000,
          isClosable: true,
        });
        // Stay on the detail page — it refetches into the `:declined`
        // read-only state with the StatusContextBanner explaining next steps.
      } catch {
        // surface handled by useMemberMutate
      }
    }, [reasonValid, decline, trimmedReason, toast]);

    const handleCancelDecline = useCallback(() => {
      setShowDecline(false);
      setDeclineReason("");
    }, []);

    // Auto-focus the textarea when the decline form opens so screen-reader and
    // keyboard users land on the input without needing to tab through.
    useEffect(() => {
      if (showDecline) {
        // Wait one frame so the Collapse animation mounts the element.
        const t = window.setTimeout(() => {
          declineTextareaRef.current?.focus();
        }, 60);
        return () => window.clearTimeout(t);
      }
    }, [showDecline]);

    const isBusy = sign.isLoading || decline.isLoading;

    const declineButton = (
      <Button
        variant="ghost"
        size="md"
        color="text.body.tertiary"
        fontWeight="600"
        minH="44px"
        onClick={() => setShowDecline((v) => !v)}
        isDisabled={isBusy}
        aria-expanded={showDecline}
        aria-controls="attestation-decline-form"
        _hover={{ bg: "neutral.100", color: "text.body.primary" }}
        _focusVisible={{
          outline: "none",
          boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
        }}
        sx={{
          transitionProperty: "background-color, color, transform",
          transitionDuration: "120ms",
          transitionTimingFunction: "ease-out",
        }}
      >
        {showDecline ? "Hide decline form" : "Decline attestation"}
      </Button>
    );

    const signButton = (
      <Button
        variant="primary"
        size="md"
        minH="44px"
        leftIcon={<Icon as={FileSignature} boxSize="16px" aria-hidden="true" />}
        onClick={handleSign}
        isLoading={sign.isLoading}
        loadingText="Signing"
        isDisabled={!allRequiredConfirmed || isBusy}
        aria-disabled={!allRequiredConfirmed || isBusy}
        _focusVisible={{
          outline: "none",
          boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
        }}
        // 0.96 scale on press for tactile feedback (only when not disabled).
        sx={{
          transitionProperty: "background-color, transform, box-shadow",
          transitionDuration: "120ms",
          transitionTimingFunction: "ease-out",
          "&:not(:disabled):active": {
            transform: "scale(0.96)",
          },
        }}
      >
        {isOwnerSignOff(attestation) ? "Attest" : "Sign attestation"}
      </Button>
    );

    return (
      <VStack w="full" align="stretch" spacing={{ base: 4, md: 5 }}>
        {/* Statement list */}
        <VStack
          w="full"
          align="stretch"
          spacing={{ base: 2, md: 3 }}
          role="group"
          aria-label="Attestation statements"
        >
          {sortedStatements.map((s, i) => (
            <StatementCard
              key={s.id}
              text={s.text}
              isRequired={s.required}
              isChecked={confirmed.has(s.id)}
              onToggle={handleToggleStatement(s.id)}
              index={i + 1}
            />
          ))}
        </VStack>

        {/* Footnote about required statements */}
        {requiredIds.length > 0 && (
          <Text fontSize="12px" color="text.body.tertiary" lineHeight="1.5">
            All <strong>Required</strong> statements must be confirmed before
            you can sign. Optional statements help auditors but are not
            mandatory.
          </Text>
        )}

        {/*
          Action row.
          On mobile, the row is sticky to the bottom of the scroll container so
          the sign + decline CTAs are always reachable. On desktop, it stays
          inline. The page body adds bottom padding so content isn't hidden
          under the bar.
        */}
        <Box
          w="full"
          position={{ base: "sticky", md: "static" }}
          bottom={{ base: 0, md: "auto" }}
          mx={{ base: -2, md: 0 }}
          px={{ base: 4, md: 0 }}
          pt={{ base: 3, md: 0 }}
          pb={{ base: 3, md: 0 }}
          // Respect iOS safe-area inset so the bar floats above the home indicator.
          style={{
            paddingBottom: isMobile
              ? "calc(env(safe-area-inset-bottom, 0px) + 12px)"
              : undefined,
          }}
          bg={{ base: "bg.container", md: "transparent" }}
          borderTop={{ base: "1px solid", md: "none" }}
          borderColor={{ base: "border.default", md: "transparent" }}
          boxShadow={{
            base: "0 -2px 12px rgba(16, 24, 40, 0.05), 0 -1px 2px rgba(16, 24, 40, 0.04)",
            md: "none",
          }}
          zIndex={{ base: 5, md: "auto" }}
        >
          <Flex
            direction={{ base: "column-reverse", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            justify="space-between"
            gap={3}
            w="full"
          >
            {declineButton}
            {signButton}
          </Flex>
        </Box>

        {/* Decline form — animates open, fixed translateY on exit */}
        <Collapse in={showDecline} animateOpacity unmountOnExit>
          <Box
            id="attestation-decline-form"
            w="full"
            bg="bg.container"
            border="1px solid"
            borderColor="border.default"
            borderRadius="12px"
            px={{ base: 4, md: 5 }}
            py={{ base: 4, md: 5 }}
            mt={{ base: 1, md: 2 }}
          >
            <VStack align="stretch" spacing={3} w="full">
              <VStack align="flex-start" spacing={1}>
                <Text
                  as="label"
                  htmlFor="attestation-decline-reason"
                  fontSize="14px"
                  fontWeight="600"
                  color="text.body.primary"
                >
                  Reason for declining
                </Text>
                <Text
                  id="attestation-decline-reason-help"
                  fontSize="12px"
                  color="text.body.tertiary"
                  lineHeight="1.5"
                >
                  Tell your admin why you can’t sign. They’ll re-open the
                  review or follow up directly. Minimum 10 characters.
                </Text>
              </VStack>
              <Textarea
                id="attestation-decline-reason"
                ref={declineTextareaRef}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g., the data appears incorrect. Please re-review the entitlement set."
                minH="120px"
                resize="vertical"
                fontSize="14px"
                lineHeight="1.5"
                isDisabled={decline.isLoading}
                aria-describedby="attestation-decline-reason-help"
                aria-required={true}
                aria-invalid={
                  declineReason.length > 0 && !reasonValid ? true : undefined
                }
                _focusVisible={{
                  borderColor: "border.focus",
                  boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
                }}
              />
              <HStack justify="flex-end" spacing={2} pt={1}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelDecline}
                  isDisabled={decline.isLoading}
                  color="text.body.tertiary"
                  fontWeight="600"
                  minH="44px"
                  px={4}
                  _hover={{ bg: "neutral.100", color: "text.body.primary" }}
                  _focusVisible={{
                    outline: "none",
                    boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmitDecline}
                  isLoading={decline.isLoading}
                  loadingText="Submitting"
                  isDisabled={!reasonValid || decline.isLoading}
                  bg="error.500"
                  color="white"
                  fontWeight="600"
                  borderRadius="8px"
                  minH="44px"
                  px={4}
                  _hover={{ bg: "error.600" }}
                  _focusVisible={{
                    outline: "none",
                    boxShadow: "0 0 0 3px var(--chakra-colors-error-200)",
                  }}
                  sx={{
                    transitionProperty: "background-color, transform",
                    transitionDuration: "120ms",
                    transitionTimingFunction: "ease-out",
                    "&:not(:disabled):active": {
                      transform: "scale(0.96)",
                    },
                  }}
                >
                  Submit decline
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Collapse>
      </VStack>
    );
  },
);
PendingActionArea.displayName = "PendingActionArea";

// ── read-only statement list (for attested/declined/superseded/skipped) ──────

const ReadOnlyStatements = memo(
  ({
    statements,
    visuallyMuted,
  }: {
    statements: MFAttestationStatement[];
    visuallyMuted?: boolean;
  }) => {
    const sorted = useMemo(() => sortStatements(statements), [statements]);
    return (
      <VStack
        w="full"
        align="stretch"
        spacing={{ base: 2, md: 3 }}
        opacity={visuallyMuted ? 0.7 : 1}
      >
        {sorted.map((s, i) => (
          <StatementCard
            key={s.id}
            text={s.text}
            isRequired={s.required}
            isChecked={s.confirmed}
            isDisabled
            index={i + 1}
          />
        ))}
      </VStack>
    );
  },
);
ReadOnlyStatements.displayName = "ReadOnlyStatements";

// ── page-level states ────────────────────────────────────────────────────────

const LoadingState = memo(() => (
  <Flex w="full" justify="center" py={20}>
    <Spinner size="md" color="text.body.tertiary" />
  </Flex>
));
LoadingState.displayName = "LoadingState";

const NotFoundState = memo(() => (
  <Box
    w="full"
    bg="bg.container"
    border="1px dashed"
    borderColor="border.default"
    borderRadius="16px"
    px={{ base: 6, md: 10 }}
    py={{ base: 10, md: 14 }}
  >
    <VStack spacing={4} textAlign="center">
      <Flex
        align="center"
        justify="center"
        w="56px"
        h="56px"
        borderRadius="full"
        bg="neutral.100"
        border="1px solid"
        borderColor="border.default"
      >
        <Icon as={Info} boxSize="24px" color="text.body.tertiary" />
      </Flex>
      <VStack spacing={2} maxW="420px">
        <Text
          fontFamily="HubotSans, sans-serif"
          fontSize="18px"
          fontWeight="500"
          color="text.body.primary"
          lineHeight="1.3"
        >
          Attestation not found
        </Text>
        <Text
          fontSize="14px"
          fontWeight="400"
          color="text.body.tertiary"
          lineHeight="1.5"
          sx={{ textWrap: "pretty" }}
        >
          The attestation you’re looking for might have been completed already
          or no longer exists.
        </Text>
      </VStack>
      <Button
        as={Link}
        to="/members/attestations"
        variant="secondary"
        size="sm"
        leftIcon={<Icon as={ArrowLeft} boxSize="14px" />}
      >
        Back to attestations
      </Button>
    </VStack>
  </Box>
));
NotFoundState.displayName = "NotFoundState";

// ── header ───────────────────────────────────────────────────────────────────

// The system's brand logo when we have one, otherwise the resource-type glyph
// on a neutral tile, falling through to a generic glyph when the type is
// unknown too. Same ladder the campaign side panel's ResourceCard walks.
const ResourceMark = memo(
  ({ attestation }: { attestation: MFAttestation }) => {
    const name = attestationTitle(attestation);

    if (attestation.resourceImageUrl) {
      return (
        <Box flexShrink={0} mt="2px">
          <AppLogo name={name} logoUrl={attestation.resourceImageUrl} size="xl" />
        </Box>
      );
    }

    return (
      <Flex
        align="center"
        justify="center"
        flexShrink={0}
        w="48px"
        h="48px"
        borderRadius="12px"
        bg="bg.section"
        mt="2px"
      >
        <ResourceIcon resourceType={attestation.resourceType || ""} size={6} />
      </Flex>
    );
  },
);
ResourceMark.displayName = "ResourceMark";

const AttestationHeader = memo(
  ({ attestation }: { attestation: MFAttestation }) => {
    const title = attestationTitle(attestation);
    const subtitle = attestationSubtitle(attestation);

    return (
      <VStack align="flex-start" spacing={4} w="full">
        <BackLink />
        <Flex
          w="full"
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={4}
        >
          <HStack spacing={4} align="flex-start" flex="1" minW={0}>
            <ResourceMark attestation={attestation} />
            <VStack align="flex-start" spacing="6px" flex="1" minW={0}>
              <Text
                fontFamily="HubotSans, sans-serif"
                fontSize={{ base: "22px", md: "28px" }}
                fontWeight="500"
                color="text.body.primary"
                lineHeight="1.2"
                sx={{ textWrap: "balance" }}
              >
                {title}
              </Text>
              <HStack
                spacing={2}
                align="center"
                flexWrap="wrap"
                rowGap="4px"
              >
                {subtitle && (
                  <>
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      color="text.body.secondary"
                      noOfLines={1}
                    >
                      {subtitle}
                    </Text>
                    <Text fontSize="14px" color="text.body.tertiary">
                      •
                    </Text>
                  </>
                )}
                <Text fontSize="14px" color="text.body.tertiary">
                  Issued {formatDate(attestation.insertedAt)}
                </Text>
              </HStack>
            </VStack>
          </HStack>

          <Box flexShrink={0}>
            <StatusBadge status={attestation.status} />
          </Box>
        </Flex>
      </VStack>
    );
  },
);
AttestationHeader.displayName = "AttestationHeader";

// ── main component ───────────────────────────────────────────────────────────

const SECTION_TITLE_PROPS = {
  fontFamily: "HubotSans, sans-serif",
  fontSize: { base: "16px", md: "18px" },
  fontWeight: "500",
  color: "text.body.primary",
  lineHeight: "1.3",
} as const;

const SECTION_KICKER_PROPS = {
  fontSize: "11px",
  fontWeight: "600",
  color: "text.body.tertiary",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
} as const;

const AttestationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: attestation, isLoading } = MF_ACCESS_REVIEWS.useGetAttestation(
    id ?? null,
  );

  if (isLoading) {
    return (
      <VStack
        w="full"
        align="stretch"
        spacing={0}
        pb={{ base: "120px", md: "160px" }}
      >
        <LoadingState />
      </VStack>
    );
  }

  if (!attestation) {
    return (
      <VStack
        w="full"
        align="stretch"
        spacing={6}
        pb={{ base: "120px", md: "160px" }}
      >
        <BackLink />
        <NotFoundState />
      </VStack>
    );
  }

  const readOnly = isReadOnly(attestation.status);
  const section = reviewSectionCopy(attestation);

  return (
    <VStack
      w="full"
      align="stretch"
      spacing={{ base: 6, md: 8 }}
      pb={{ base: "120px", md: "160px" }}
      maxW="900px"
    >
      {/* Section 1 — Header */}
      <AttestationHeader attestation={attestation} />

      <StatusContextBanner attestation={attestation} />

      <Divider borderColor="border.default" />

      {/* Section 2 — Read-only review summary (or sign-off explainer) */}
      <VStack align="stretch" spacing={{ base: 3, md: 4 }} w="full">
        <VStack align="flex-start" spacing={1}>
          <Text {...SECTION_KICKER_PROPS}>{section.kicker}</Text>
          <Text {...SECTION_TITLE_PROPS}>{section.title}</Text>
          <Text
            fontSize="13px"
            color="text.body.tertiary"
            lineHeight="1.5"
            sx={{ textWrap: "pretty" }}
          >
            {section.body}
          </Text>
        </VStack>
        {section.showSummary && (
          <>
            <ReviewSummaryBlock summary={attestation.reviewSummary} />
            <ReviewDrilldownLink reviewId={attestation.accessReviewId} />
          </>
        )}
      </VStack>

      {showsEvidencePanel(attestation) && (
        <>
          <Divider borderColor="border.default" />

          {/* Section 2b — Optional evidence (owner sign-off only, PLO-5185) */}
          <VStack align="stretch" spacing={{ base: 3, md: 4 }} w="full">
            <VStack align="flex-start" spacing={1}>
              <Text {...SECTION_KICKER_PROPS}>{EVIDENCE_KICKER}</Text>
              <Text {...SECTION_TITLE_PROPS}>Attach supporting evidence</Text>
              <Text
                fontSize="13px"
                color="text.body.tertiary"
                lineHeight="1.5"
                sx={{ textWrap: "pretty" }}
              >
                {EVIDENCE_BODY}
              </Text>
            </VStack>
            <AttestationEvidence
              reviewId={attestation.accessReviewId}
              readOnly={!evidenceEditable(attestation)}
            />
          </VStack>
        </>
      )}

      <Divider borderColor="border.default" />

      {/* Section 3 — Sign / Decline (or read-only statements) */}
      <VStack align="stretch" spacing={{ base: 3, md: 4 }} w="full">
        <VStack align="flex-start" spacing={1}>
          <Text {...SECTION_KICKER_PROPS}>
            {readOnly ? "Statements" : "Your attestation"}
          </Text>
          <Text {...SECTION_TITLE_PROPS}>
            {readOnly
              ? "Statements on this attestation"
              : "Confirm and sign"}
          </Text>
          <Text
            fontSize="13px"
            color="text.body.tertiary"
            lineHeight="1.5"
            sx={{ textWrap: "pretty" }}
          >
            {readOnly
              ? "These are the statements that were on the attestation."
              : "Read each statement carefully. Required ones must be confirmed before you can sign."}
          </Text>
        </VStack>

        {readOnly ? (
          <ReadOnlyStatements
            statements={attestation.statements}
            visuallyMuted={
              attestation.status === "declined" ||
              attestation.status === "superseded"
            }
          />
        ) : (
          <PendingActionArea attestation={attestation} />
        )}
      </VStack>
    </VStack>
  );
};

export default AttestationDetail;
